import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BadgeEditModal = ({ isOpen, onClose, badge, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'learning',
    points: 10,
    image_url: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const categoryOptions = [
    { value: 'learning', label: 'Learning' },
    { value: 'streak', label: 'Streak' },
    { value: 'mastery', label: 'Mastery' },
    { value: 'special', label: 'Special' }
  ];

  // Update form data when badge changes
  useEffect(() => {
    if (badge) {
      setFormData({
        title: badge?.title || '',
        description: badge?.description || '',
        category: badge?.category || 'learning',
        points: badge?.points || 10,
        image_url: badge?.image_url || '',
        is_active: badge?.is_active !== undefined ? badge?.is_active : true
      });
    }
  }, [badge]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const success = await onSave({
        ...formData,
        points: parseInt(formData?.points) || 10
      });

      if (success) {
        onClose();
      }
    } catch (err) {
      setError('Failed to update badge. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            Edit Badge
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <Icon name="X" size={20} className="text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-red-600" />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Badge Title *
            </label>
            <Input
              id="title"
              name="title"
              type="text"
              value={formData?.title}
              onChange={handleInputChange}
              placeholder="Enter badge title"
              required
              disabled={loading}
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData?.description}
              onChange={handleInputChange}
              placeholder="Describe what this badge represents"
              required
              disabled={loading}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <Select
              id="category"
              name="category"
              value={formData?.category}
              onChange={handleInputChange}
              options={categoryOptions}
              required
              disabled={loading}
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="points" className="block text-sm font-medium text-gray-700 mb-1">
              Points
            </label>
            <Input
              id="points"
              name="points"
              type="number"
              value={formData?.points}
              onChange={handleInputChange}
              placeholder="10"
              min="1"
              max="1000"
              disabled={loading}
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="image_url" className="block text-sm font-medium text-gray-700 mb-1">
              Image URL (Optional)
            </label>
            <Input
              id="image_url"
              name="image_url"
              type="url"
              value={formData?.image_url}
              onChange={handleInputChange}
              placeholder="https://example.com/badge-image.png"
              disabled={loading}
              className="w-full"
            />
          </div>

          <div className="flex items-center">
            <input
              id="is_active"
              name="is_active"
              type="checkbox"
              checked={formData?.is_active}
              onChange={handleInputChange}
              disabled={loading}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
              Active (badge can be earned)
            </label>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading || !formData?.title || !formData?.description}
              className="flex-1"
            >
              {loading ? 'Updating...' : 'Update Badge'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BadgeEditModal;